import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import type { TournamentStatus, TournamentSummary } from '../domain';
import { useTournament } from '../state/TournamentContext';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from '../state/I18nContext';
import { DeleteTournamentDialog } from './DeleteTournamentDialog';

const STATUS_COLOR: Record<TournamentStatus, 'default' | 'info' | 'success'> = {
  DRAFT: 'default',
  IN_PROGRESS: 'info',
  FINISHED: 'success',
};

interface TournamentsViewProps {
  onNew: () => void;
}

export function TournamentsView({ onNew }: TournamentsViewProps) {
  const { summaries, companies, select, remove } = useTournament();
  const { hasPermission } = useAuth();
  const { t, localeCode } = useTranslation();
  const [pendingDelete, setPendingDelete] = useState<TournamentSummary | null>(null);
  // Company filter: 'all' shows every accessible tournament.
  const [companyFilter, setCompanyFilter] = useState<number | 'all'>('all');

  // Buttons are gated by what the signed-in user may actually do; the server
  // enforces the same rules on every request.
  const canCreate = hasPermission('CREATE_TOURNAMENT');
  const canDelete = hasPermission('DELETE_TOURNAMENT');

  const companyNames = useMemo(
    () => new Map<number, string>(companies.map((c) => [c.id, c.name])),
    [companies],
  );

  const visible = useMemo(
    () =>
      companyFilter === 'all'
        ? summaries
        : summaries.filter((summary) => summary.companyId === companyFilter),
    [summaries, companyFilter],
  );

  const confirmDelete = () => {
    if (pendingDelete) void remove(pendingDelete.id);
    setPendingDelete(null);
  };

  return (
    <Box sx={{ maxWidth: 880, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {t('admin.tournaments.title')}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="tournaments-company-filter-label">
            {t('admin.tournaments.companyFilter')}
          </InputLabel>
          <Select
            labelId="tournaments-company-filter-label"
            label={t('admin.tournaments.companyFilter')}
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value as number | 'all')}
          >
            <MenuItem value="all">{t('admin.tournaments.allCompanies')}</MenuItem>
            {companies.map((company) => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={onNew}>
            {t('admin.tournaments.newTournament')}
          </Button>
        )}
      </Stack>

      {summaries.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {t('admin.tournaments.empty.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('admin.tournaments.empty.body')}
          </Typography>
        </Paper>
      ) : visible.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {t('admin.tournaments.emptyForCompany')}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {visible.map((summary) => (
            <Paper
              key={summary.id}
              variant="outlined"
              sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  flexShrink: 0,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'action.hover',
                  display: 'grid',
                  placeItems: 'center',
                  ...(summary.coverUrl
                    ? {
                        backgroundImage: `url(${summary.coverUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }
                    : {}),
                }}
              >
                {!summary.coverUrl && (
                  <EmojiEventsIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                )}
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" noWrap title={summary.name}>
                  {summary.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {companyNames.get(summary.companyId) ??
                    t('admin.tournaments.companyFallback', { id: summary.companyId })}{' '}
                  ·{' '}
                  {summary.sportId === 'FOOTBALL'
                    ? t('admin.tournaments.teamCount', { count: summary.totalPlayers })
                    : t('admin.tournaments.playerCount', { count: summary.totalPlayers })}{' '}
                  ·{' '}
                  {new Date(summary.createdAt).toLocaleString(localeCode)}
                </Typography>
              </Box>
              <Chip
                size="small"
                variant="outlined"
                label={t(`sports.${summary.sportId}`)}
              />
              <Chip
                size="small"
                color={STATUS_COLOR[summary.status]}
                label={t(`admin.common.status.${summary.status}`)}
              />
              <Button variant="outlined" onClick={() => void select(summary.id)}>
                {t('admin.tournaments.open')}
              </Button>
              {canDelete && (
                <IconButton
                  aria-label={t('admin.tournaments.deleteAria', { name: summary.name })}
                  onClick={() => setPendingDelete(summary)}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              )}
            </Paper>
          ))}
        </Stack>
      )}

      <DeleteTournamentDialog
        open={pendingDelete !== null}
        status={pendingDelete?.status ?? 'DRAFT'}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </Box>
  );
}
